// Mock data for Hassan M Allahham's portfolio (multi-track executive positioning)

export const profile = {
  name: "Hassan M Allahham",
  initials: "HA",
  eyebrow: "Executive Leadership · UAE · KSA · Oman · AI-Forward",
  title: "Business, Technology & Operations Executive",
  location: "Abu Dhabi, UAE",
  email: "ha@hassan-allahham.com",
  phone: "+971 50 417 5663",
  phoneRaw: "971504175663",
  linkedin: "https://www.linkedin.com/in/hassan-allahham-1b14a040/",
  photo: "/assets/hassan-portrait.jpg",
  resumeUrl: "/assets/hassan-cv.pdf",
  headline:
    "Business, Technology & Operations Executive — building growth engines for regional enterprises.",
  subline:
    "15+ years leading managed services, IT operations, and commercial growth across the GCC. I step into Operations, Technology, or Commercial leadership roles and turn service organizations into measurable engines of revenue, efficiency, and customer trust.",
  pullQuote:
    "I turn regional service operations into engines of growth — across operations, technology, or commercial leadership.",
  about: [
    "Hassan Allahham is a senior executive with over 15 years leading managed services, IT operations, and digital transformation across the UAE, KSA, and Oman. He has built and run regional organizations from the ground up — owning P&L, leading multidisciplinary teams of 100+, and turning service delivery into a driver of growth rather than a cost center. He is recognized as an AI‑forward operator, translating automation and emerging technology into measurable commercial and operational outcomes.",
    "At Ghobash Group (GCG / GBS / CNS), Hassan led the regional service strategy that lifted operational efficiency by 96% and doubled revenue within three years, while sustaining 98% customer retention across 2,000 clients and 25,000 managed devices. His background spans presales and solution architecture, service and P&L management, cybersecurity and infrastructure governance, and commercial growth — giving him the range to lead as a Managing Director, Country Manager, Chief Technology/Information Officer, Chief Commercial Officer, or Head of Service Delivery.",
    "He holds an MBA (University of Greater Manchester, UK), an L7 Diploma in Strategic Leadership & Management, and a B.Sc. in Computer Information Systems, backed by Six Sigma, PMP, ITIL v4, MCSE and UiPath credentials. He is fluent in English and Arabic and based in Abu Dhabi, UAE."
  ],
  languages: [
    { name: "English", level: "Proficient" },
    { name: "Arabic", level: "Native" }
  ]
};

// 8-tile proof bar directly under the hero
export const proofBar = [
  { value: "15+", label: "Years in Managed Services & Digital Transformation" },
  { value: "200%", label: "Revenue Growth in 3 Years" },
  { value: "96%", label: "Operational Efficiency Gain" },
  { value: "98%", label: "Customer Retention Rate" },
  { value: "100+", label: "Team Members Led Across 3 Markets" },
  { value: "2,000", label: "Clients · 25,000 Devices Managed" },
  { value: "32%", label: "Cost Reduction Delivered" },
  { value: "3", label: "Countries — UAE · KSA · Oman" }
];

// Kept for backwards compatibility (unused by proof bar now)
export const metrics = proofBar;

// Leadership Tracks — the multi-track executive positioning
export const leadershipTracks = [
  {
    id: "ops",
    key: "operations",
    label: "Operations & GM",
    title: "Operations & General Management",
    targetTitles: [
      "Managing Director",
      "Country Manager",
      "General Manager",
      "COO",
      "Business Operations Head"
    ],
    pitch:
      "Full P&L ownership and regional operating leadership across multi‑market environments. Builds operating models, leads multidisciplinary teams, and scales business performance from strategy to execution.",
    proof:
      "96% regional operating‑efficiency gain; 200% revenue growth in 3 years; P&L accountability for a 100+ person organization across UAE, KSA & Oman."
  },
  {
    id: "tech",
    key: "technology",
    label: "Technology & Digital",
    title: "Technology & Digital Transformation",
    targetTitles: [
      "SVP Technology",
      "CIO",
      "IT Director",
      "Head of IT / Infrastructure & Cybersecurity"
    ],
    pitch:
      "Enterprise technology leadership spanning infrastructure, cybersecurity governance, ITIL‑based service management, and AI‑forward digital transformation programs.",
    proof:
      "Delivered 20+ new digital solutions to market; led infrastructure and managed‑services governance for 2,000+ clients; ITIL v4, MCSE and UiPath (automation) certified."
  },
  {
    id: "commercial",
    key: "commercial",
    label: "Commercial & Growth",
    title: "Commercial & Business Growth",
    targetTitles: [
      "Chief Commercial Officer",
      "Sales Director",
      "Business Development Director",
      "Channel & Partner Director"
    ],
    pitch:
      "Revenue and market‑expansion leadership — building partner ecosystems, leading strategic accounts, and translating market demand into profitable growth.",
    proof:
      "AED 60M+ in annual deals supported; 80% increase in proposal‑to‑close conversion; strategic partnerships with Kyocera, Epson, Samsung, HP, Microsoft, Nintex."
  },
  {
    id: "delivery",
    key: "delivery",
    label: "Service & Programs",
    title: "Service Delivery & Program Leadership",
    targetTitles: [
      "Head of Service Delivery",
      "PMO / Programme Director",
      "Managed Services Practice Director"
    ],
    pitch:
      "Governance‑led delivery leadership for large, complex, multi‑year programs — SLA/KPI management, service transformation, and continuous improvement at national scale.",
    proof:
      "Managed the UAE's largest school managed‑print program (500+ schools, 40,000 users) and a 16‑company ADNOC master services agreement, lifting delivery performance by 35%."
  },
  {
    id: "ai",
    key: "ai",
    label: "AI & Innovation",
    title: "AI & Digital Innovation",
    targetTitles: [
      "Chief AI Officer",
      "VP AI & Automation",
      "Head of Digital Innovation",
      "AI / Automation Transformation Director"
    ],
    pitch:
      "Translates emerging technology — AI, RPA, and intelligent workflow automation — into commercial outcomes. Leads the shift from manual service operations to automation‑led, data‑driven delivery models.",
    proof:
      "Automation‑led value propositions contributed directly to 200% revenue growth in 3 years; UiPath (RPA) certified; led enterprise workflow automation deployments on Nintex, K2 and OutSystems platforms across 2,000+ client environments."
  }
];

// Experience — rewritten in executive voice
export const experience = [
  {
    id: "exp-1",
    company: "Ghobash Group — GCG, GBS, CNS",
    title: "Service Director / Regional ICT Service & Operations Director",
    period: "2020 — Present",
    location: "UAE · KSA · Oman",
    focus: "Regional service & operations strategy · P&L · Multi‑market leadership",
    points: [
      "Own regional service and operations strategy across three markets, lifting operational efficiency by 96% and doubling revenue within three years.",
      "Lead a multidisciplinary organization of 100+ — service engineering, sales, and billing — with full P&L accountability, delivering 10% YoY growth and a 32% reduction in operating costs.",
      "Direct service delivery to 2,000 clients and 25,000 managed devices, sustaining 98% customer retention through structured satisfaction and governance programs.",
      "Manage strategic vendor, supplier and partner relationships that underpin regional delivery capability."
    ]
  },
  {
    id: "exp-2",
    company: "GCG Enterprise Solutions",
    title: "Solutions and Managed Services Head",
    period: "2015 — 2020",
    location: "Dubai, UAE",
    focus: "Presales & technical delivery · Solutions portfolio · Regional GTM",
    points: [
      "Led presales and technical delivery across UAE, KSA & Oman, expanding the solutions portfolio by 20+ offerings and lifting market competitiveness by 25%.",
      "Delivered 300+ regional projects and led a 15‑person technical/presales team, helping position the company among the UAE's top three managed print service providers.",
      "Built and managed strategic relationships with global technology partners including Kyocera, Epson, Samsung, HP, Microsoft and Nintex."
    ]
  },
  {
    id: "exp-3",
    company: "Gulf Commercial Group — Office Imaging",
    title: "MPS Presales Consultant",
    period: "2011 — 2015",
    location: "Abu Dhabi, UAE",
    focus: "Enterprise proposals · Solution selling · MPS advisory",
    points: [
      "Developed tailored managed print service proposals that increased client conversion by 80%, presenting to 800+ prospective clients.",
      "Partnered with sales teams to close AED 60M in annual business, while analysis‑driven cost proposals cut client print expenses by up to 30%."
    ]
  },
  {
    id: "exp-4",
    company: "Jordan Education Initiative",
    title: "Project Leader",
    period: "2007 — 2009",
    location: "Amman, Jordan",
    focus: "Technology in education · Stakeholder management",
    points: [
      "Led technology‑in‑education deployment across 100 schools, securing a 20% increase in project funding and lifting student engagement by 30% through data‑driven program design."
    ]
  },
  {
    id: "exp-5",
    company: "ALWAFAA EST.",
    title: "IT Systems Administrator",
    period: "2003 — 2009",
    location: "Amman, Jordan",
    focus: "Infrastructure · Security · Uptime",
    points: [
      "Managed IT infrastructure and resources, reducing downtime by 30% and implementing security protocols that strengthened data protection."
    ]
  }
];

export const signatureProjects = [
  {
    id: "p1",
    title: "UAE Ministry of Education — National Managed Print Program",
    tag: "Public Sector · National Scale",
    description:
      "End‑to‑end delivery of the UAE's largest managed print services contract: 500+ schools and 40,000 users."
  },
  {
    id: "p2",
    title: "ADNOC Group Services Agreement",
    tag: "Energy · Enterprise",
    description:
      "Executed the largest master agreement of its kind, servicing 16 ADNOC group companies and lifting service delivery performance by 35%."
  },
  {
    id: "p3",
    title: "Global Embassy Managed Print Network",
    tag: "Diplomatic · Global",
    description:
      "Proposed, launched and managed a geographically distributed managed print service spanning 160+ embassies worldwide."
  },
  {
    id: "p4",
    title: "FujiFilm UAE Market Entry",
    tag: "New Product · Market Launch",
    description:
      "Introduced FujiFilm to the UAE market and secured its first premium managed services contract."
  }
];

// Grouped capability clusters (replaces flat skill tags)
export const capabilities = [
  {
    group: "Leadership & Strategy",
    items: [
      "P&L Management",
      "Growth Strategy",
      "Regional Operations",
      "Team Leadership (100+)",
      "Strategic Partnerships",
      "Governance"
    ]
  },
  {
    group: "Technology & Digital Transformation",
    items: [
      "Digital Transformation",
      "IT Infrastructure",
      "Cybersecurity Governance",
      "Enterprise Content Management",
      "RPA / Automation (UiPath)",
      "ITIL v4"
    ]
  },
  {
    group: "Commercial & Growth",
    items: [
      "Business Development",
      "Channel & Partner Management",
      "Presales & Solution Selling",
      "Strategic Accounts",
      "Market Expansion"
    ]
  },
  {
    group: "Delivery & Process",
    items: [
      "Service Delivery Management",
      "Process Optimization",
      "Six Sigma",
      "PMO / Programme Delivery",
      "Customer Retention Programs"
    ]
  },
  {
    group: "AI & Automation",
    items: [
      "RPA / Intelligent Automation (UiPath)",
      "Workflow Automation (Nintex, K2, OutSystems)",
      "AI‑Enabled Service Delivery",
      "Emerging Technology Go‑to‑Market",
      "Data‑Driven Decision Making"
    ]
  }
];

// Kept alias for older references
export const skillGroups = capabilities;

export const training = ["Six Sigma", "PMP", "EVA", "ITIL v4", "MCSE", "UiPath"];

// Technology & Vendor Ecosystem — grouped by domain
export const vendorEcosystem = [
  {
    id: "print",
    category: "Managed Print & Digital Imaging",
    caption:
      "Enterprise imaging fleets, MPS deployments and hardware standards across the region.",
    vendors: [
      { name: "HP", alt: "HP — Managed Print & Enterprise Imaging" },
      { name: "Kyocera", alt: "Kyocera — Managed Print Services" },
      { name: "FujiFilm", alt: "FujiFilm — Premium Managed Print Services" },
      { name: "Epson", alt: "Epson — Business Inkjet & Digital Imaging" },
      { name: "Samsung", alt: "Samsung — Enterprise Printing" },
      { name: "Brother", alt: "Brother — Office Printing Solutions" },
      { name: "Kodak", alt: "Kodak — Production Print & Imaging" }
    ]
  },
  {
    id: "workflow",
    category: "Print Management, Document Workflow & Enterprise Platforms",
    caption:
      "Print governance, document workflow, low‑code and enterprise application platforms.",
    vendors: [
      { name: "PaperCut", alt: "PaperCut — Print Management Software" },
      { name: "MyQ", alt: "MyQ — Print Management & Workflow" },
      { name: "SafeQ", alt: "SafeQ (YSoft) — Enterprise Print Management" },
      { name: "Nintex", alt: "Nintex — Workflow Automation & Process Management" },
      { name: "K2", alt: "K2 (Nintex K2) — Enterprise Workflow Platform" },
      { name: "OutSystems", alt: "OutSystems — Low‑Code Enterprise Application Platform" },
      { name: "Salesforce", alt: "Salesforce — CRM & Customer Platform" },
      { name: "Microsoft Dynamics", alt: "Microsoft Dynamics — ERP & CRM" }
    ]
  },
  {
    id: "networking",
    category: "Networking & Infrastructure",
    caption:
      "Compute, connectivity, data‑centre and edge infrastructure standards.",
    vendors: [
      { name: "Cisco", alt: "Cisco — Enterprise Networking & Security" },
      { name: "Dell", alt: "Dell — Server, Storage & End‑User Compute" },
      { name: "Hewlett Packard Enterprise", alt: "HPE — Enterprise Infrastructure & Compute" },
      { name: "Huawei", alt: "Huawei — Networking & Enterprise Infrastructure" },
      { name: "APC", alt: "APC — Power Protection & Data Centre Infrastructure" },
      { name: "Lenovo", alt: "Lenovo — Server & End‑User Compute" },
      { name: "Ruijie", alt: "Ruijie Networks — Networking Infrastructure" },
      { name: "Fortinet", alt: "Fortinet — Network Security & SD‑WAN" }
    ]
  },
  {
    id: "security",
    category: "Cybersecurity & Endpoint Resilience",
    caption:
      "Endpoint, email, network and application security across regulated GCC clients.",
    vendors: [
      { name: "Absolute", alt: "Absolute — Endpoint Resilience & Recovery" },
      { name: "Sophos", alt: "Sophos — Endpoint & Network Security" },
      { name: "Darktrace", alt: "Darktrace — AI‑Powered Cyber Defence" },
      { name: "Kaspersky", alt: "Kaspersky — Endpoint & Threat Protection" },
      { name: "Trend Micro", alt: "Trend Micro — Endpoint & Cloud Security" },
      { name: "Barracuda", alt: "Barracuda — Email & Network Security" },
      { name: "Rapid7", alt: "Rapid7 — Vulnerability & Threat Detection" },
      { name: "Invicti", alt: "Invicti — Application Security Testing" },
      { name: "ESET", alt: "ESET — Endpoint Protection" },
      { name: "Heimdal", alt: "Heimdal Security — Unified Threat Prevention" },
      { name: "Motadata", alt: "Motadata — IT Operations & Security Monitoring" }
    ]
  },
  {
    id: "continuity",
    category: "Business Continuity & Smart Infrastructure",
    caption:
      "Data protection, visual collaboration and physical/smart security estates.",
    vendors: [
      { name: "Veeam", alt: "Veeam — Backup & Data Protection" },
      { name: "ViewSonic", alt: "ViewSonic — Visual Collaboration & Displays" },
      { name: "Hikvision", alt: "Hikvision — Video Surveillance & Smart Security" },
      { name: "Dahua Technology", alt: "Dahua Technology — Video Surveillance & Smart Solutions" }
    ]
  }
];

export const education = [
  {
    degree: "Master of Business Administration (MBA)",
    school: "University of Greater Manchester, UK",
    year: "2026"
  },
  {
    degree: "L7 Diploma — Strategic Leadership & Management",
    school: "WINC, London",
    year: "2024"
  },
  {
    degree: "B.Sc. Computer Information Systems",
    school: "Al‑Zaytoonah University of Jordan",
    year: "2007"
  }
];

// Press & Media — Public articles (unchanged)
export const press = [
  {
    id: "pr1",
    source: "TahawulTech · CNME",
    date: "February 2024",
    title:
      "“Our new strategic initiative is aimed at offering a full spectrum of Managed IT Services to our clients.”",
    summary:
      "Exclusive interview with CNME Editor Mark Forker, where I laid out GCG Enterprise Solutions' expansion from a leading MPS provider into the preferred Managed IT Services partner for the GCC SME community — covering cloud, infrastructure, helpdesk, maintenance and IT outsourcing.",
    quote:
      "The SME market needs an experienced partner that can deliver enterprise‑grade, AI‑driven services, without the burden of the typical costs associated with enterprise level service providers.",
    href: "https://www.tahawultech.com/interviews/our-new-strategic-initiative-is-aimed-at-offering-a-full-spectrum-of-managed-it-services-to-our-clients-gcg-enterprise-solutions/",
    tag: "Interview"
  },
  {
    id: "pr2",
    source: "AETOSWire",
    date: "29 July 2025",
    title: "GCG Enterprise Solutions Unveils Dedicated IT Services Website",
    summary:
      "Announcement marking GCG's strategic evolution from a traditional print services provider to a comprehensive IT solutions leader — featuring my quote on the transformation and the launch of www.gcg.ae/IT.",
    quote:
      "Our transformation into a full‑fledged IT Services provider has been met with strong market adoption, and this new platform will further empower our clients to explore innovative solutions and connect with our experts.",
    href: "https://www.aetoswire.com/en/news/gcg02972025en",
    tag: "Announcement"
  },
  {
    id: "pr3",
    source: "Ghobash Group",
    date: "21 November 2024",
    title:
      "GCG Enterprise Solutions Announces Strategic Partnership with Forenzy Networks",
    summary:
      "Strategic partnership fortifying GCG's IT Services portfolio with globally certified cybersecurity and digital forensics capabilities — covering vCISO, VAPT, red‑teaming, cloud auditing and mobile security auditing.",
    quote:
      "We welcome the addition of Forenzy Networks to our IT Services portfolio. Our enhanced focus on cyber security services is aimed at meeting the growing demand of regional businesses.",
    href: "https://ghobash.com/gcg-enterprise-solutions-announces-strategic-partnership-with-forenzy-networks/",
    tag: "Partnership"
  }
];

// Articles (unchanged content, kept for the Insights section)
export const articles = [
  {
    id: "a1",
    slug: "from-cost-center-to-revenue-engine",
    title: "From Cost Center to Revenue Engine: Rewriting the Service P&L",
    excerpt:
      "How re‑framing managed services around outcomes — not tickets — unlocked 200% revenue growth and a 10% YoY P&L uplift.",
    category: "Strategy & P&L",
    date: "July 2025",
    readTime: "7 min read",
    body: [
      "Most service organisations are still measured like back‑offices: budgets, SLAs, headcount, utilisation. But the businesses that actually win in the GCC today are the ones that refuse to be a cost centre.",
      "Over the last three years we rebuilt our commercial model around three ideas: outcome‑priced contracts, cross‑sell pathways into digital transformation, and a service catalogue that made it easy for customers to say yes. The compound effect was 200% revenue growth and sustained 10% YoY P&L expansion.",
      "The real unlock isn't a product — it's the operating cadence. Weekly revenue reviews with delivery leaders, monthly margin deep dives by account, and quarterly portfolio rationalisation. When engineers understand the economics of what they're shipping, service becomes a growth engine.",
      "If you are leading a service P&L today, start with one question: which contracts would we proudly renew at a higher price next year? That is the line above which your real business lives."
    ]
  },
  {
    id: "a2",
    slug: "96-percent-efficiency-playbook",
    title: "The 96% Efficiency Playbook: Scaling Delivery Across UAE, KSA & Oman",
    excerpt:
      "A regional operations turnaround, told in four moves: standardise, instrument, empower, automate.",
    category: "Operations",
    date: "June 2025",
    readTime: "9 min read",
    body: [
      "Efficiency is almost always a measurement problem before it becomes a process problem. When we started the GCC turnaround, three countries were running three different definitions of 'resolved'. We couldn't improve what we couldn't agree on.",
      "Move one was standardisation: a single service taxonomy, a single resolution definition, a single escalation ladder. Move two, instrumentation: live dashboards per country, per account, per engineer — visible to everyone, not locked in a PMO deck.",
      "Move three was empowerment — pushing decision rights down to regional leads with clear financial guardrails. Move four was selective automation: triage, dispatch, and invoicing. By the end, efficiency was up 96% and, more importantly, our people actually trusted the numbers.",
      "Transformation isn't about buying tools. It's about giving your teams a shared truth they can act on before 10 a.m."
    ]
  },
  {
    id: "a3",
    slug: "ecm-digital-transformation-guide",
    title: "ECM as the Backbone of Digital Transformation — A GCC Practitioner's View",
    excerpt:
      "Why Enterprise Content Management is the single most underrated lever for digital transformation in the region — and the blueprint we follow.",
    category: "Digital Transformation",
    date: "May 2025",
    readTime: "8 min read",
    body: [
      "Every GCC enterprise I meet has a digital transformation strategy. Very few have an ECM strategy. That gap is where most transformation programmes quietly fail.",
      "Enterprise Content Management is not document scanning. It is the discipline of capturing, classifying, governing and surfacing every piece of business content — contracts, invoices, HR files, vendor records — so that downstream systems (ERP, CRM, analytics) actually have clean, trusted inputs.",
      "The blueprint we apply is simple: capture at source, classify with AI, route by policy, retain by regulation, retire by schedule. Anything else is theatre. Clients who get this right typically see 40–60% cycle‑time reductions on core processes and dramatic drops in audit findings.",
      "The executive case is even simpler: if your data is trapped in PDFs and shared drives, your AI investments will underperform. ECM is the unglamorous foundation that makes every other transformation dollar work harder."
    ]
  },
  {
    id: "a4",
    slug: "rpa-pragmatists-playbook",
    title: "RPA for Pragmatists: Where Bots Earn Their Keep in GCC Enterprises",
    excerpt:
      "Seven years in, RPA is past the hype cycle. Here is how I decide which processes get a bot — and which should be redesigned instead.",
    category: "Automation",
    date: "April 2025",
    readTime: "7 min read",
    body: [
      "Robotic Process Automation has matured from boardroom buzzword to quiet workhorse. The organisations winning with it aren't the ones with the most bots — they are the ones with the most disciplined selection criteria.",
      "Our rule of three: a process is a candidate for RPA only if it is high‑volume, rules‑based, and stable. Anything else either deserves a redesign or a human with better tooling. We have killed more bot ideas than we have shipped, and our programme is healthier for it.",
      "The real value lives at the seams — between ERP and CRM, between finance and HR, between email and workflow. Bots bridge the last mile where integration projects are too expensive or too slow. Finance reconciliations, vendor onboarding, invoice matching, licence renewals — all fair game.",
      "The deeper lesson: RPA is a governance exercise, not a technology exercise. Without a central control room, a bot factory becomes technical debt with a friendly face."
    ]
  },
  {
    id: "a5",
    slug: "low-code-no-code-reality-check",
    title: "Low‑Code, No‑Code: A Reality Check for CIOs in the GCC",
    excerpt:
      "The most useful question is no longer 'should we use low‑code?' — it's 'where should citizen developers stop?'",
    category: "Digital Transformation",
    date: "March 2025",
    readTime: "6 min read",
    body: [
      "Low‑code and no‑code platforms have quietly become the fastest way to ship internal apps in the GCC. I have seen mid‑sized organisations cut internal request backlogs from 18 months to 6 weeks. That is a serious business outcome.",
      "But every CIO I speak to is wrestling with the same risk: shadow IT wearing a vendor‑approved badge. When every department builds its own apps, data silos, security gaps and renewal surprises show up 24 months later.",
      "The governance pattern that works: a joint IT/business 'platform team' owns the environment, the integrations, the identity, the data model. Business teams build on top — with clear guardrails on what leaves the platform and where customer data can land.",
      "Treat low‑code like a regulated market. Let a thousand flowers bloom — but inside a well‑designed greenhouse."
    ]
  },
  {
    id: "a6",
    slug: "printing-industry-oems-decoded",
    title: "The Printing Industry Decoded: Kyocera, Konica Minolta, Xerox, Epson, FujiFilm",
    excerpt:
      "After 15 years working with every major OEM in the region, here is how I think about their strengths and where each wins.",
    category: "Printing Industry",
    date: "February 2025",
    readTime: "10 min read",
    body: [
      "The enterprise print market has quietly become one of the most interesting OEM landscapes in technology. Each major brand is making a different bet — and understanding those bets matters if you're buying, selling, or managing print fleets at scale.",
      "Kyocera plays the long‑life, low total‑cost‑of‑ownership game. Their ceramic drums and component longevity translate into predictable run costs and strong TCO stories for finance — a reason they remain a staple in large public sector and education deployments.",
      "Konica Minolta leans into the bizhub i‑Series and production print. Strong colour fidelity, aggressive workflow software integration and a credible play in intelligent document processing. Where the client's story is 'make print smarter', Konica is usually on the shortlist.",
      "Xerox is the workflow incumbent with the deepest legacy in office imaging and managed services. Their strength is process — ConnectKey apps, fleet analytics, and security at scale for regulated industries.",
      "Epson disrupted the market with business inkjet — quietly compelling on energy consumption, sustainability metrics and cost‑per‑page for mid‑volume environments. A genuine alternative to laser where ESG is in the conversation.",
      "FujiFilm, re‑entered as a premium play in the UAE under our leadership. Exceptional build quality, strong colour, and a credible high‑end managed services offering. We secured the first premium MPS contract in the region and the feedback has been consistently positive.",
      "The honest answer to 'which OEM is best' is always: best for what, over what horizon, against which workflow? Print strategy is portfolio strategy. Mix deliberately."
    ]
  },
  {
    id: "a7",
    slug: "managed-it-services-gcc-sme",
    title: "Managed IT Services in the GCC: The SME Opportunity Nobody Is Serving Well",
    excerpt:
      "Enterprise‑grade services delivered at SME‑friendly price points — the strategic space I believe will define the next five years.",
    category: "Managed IT Services",
    date: "January 2025",
    readTime: "7 min read",
    body: [
      "The SME market in the GCC is deeply technology‑dependent and ferociously cost‑sensitive. Most enterprise‑grade providers won't serve them economically. Most SME providers can't deliver enterprise‑grade rigour. That gap is the strategic opportunity of the decade.",
      "Our thesis at GCG was straightforward: take the platform, tooling and playbooks built for enterprise clients, and package them into consumption models that an SME finance director can actually approve. Cloud, IT infrastructure, helpdesk, maintenance, IT outsourcing — delivered with the discipline of a global shop and the economics of a local one.",
      "The market response has been telling. SMEs do not want a reseller — they want a partner who can make technology decisions they can stand behind. Brand‑agnostic, tailor‑made, and accountable.",
      "Five years from now, the winners in GCC managed IT will be the organisations that mastered this translation: enterprise rigour, SME accessibility. Everything else will look like a legacy model."
    ]
  },
  {
    id: "a8",
    slug: "cybersecurity-cloud-managed-services",
    title: "Cloud, Cybersecurity and the New Shape of GCC Managed Services",
    excerpt:
      "Why the partnership with Forenzy Networks, the pivot to cloud, and the rise of regulated data hosting are reshaping the service model.",
    category: "Managed IT Services",
    date: "December 2024",
    readTime: "6 min read",
    body: [
      "For years, managed services in the region meant print, endpoint and basic infrastructure support. Today, the centre of gravity has shifted. Cloud workloads, regulated data hosting, and cyber resilience are now the conversation.",
      "Our recent partnership with Forenzy Networks reflects this shift. Globally certified cybersecurity and digital forensics talent — CEH, CREST, OSCP — delivered under a single commercial umbrella with our existing service capability. That combination is what today's regional buyer actually asks for.",
      "The next layer is regulated hosting. Government, healthcare and financial services across the GCC now expect in‑country data residency, auditable controls and clear sovereignty. Service providers that cannot articulate their stance on this are already losing renewals.",
      "The new shape of managed services in the region: cloud‑native, security‑first, compliance‑aware, and commercially flexible. The providers who evolve their talent and their contracts together will define the next decade."
    ]
  },
  {
    id: "a9",
    slug: "customer-retention-at-98-percent",
    title: "Customer Retention at 98%: What the GCC Enterprise Market Taught Me",
    excerpt:
      "Retention is an engineering problem, not a loyalty program. Here is the scaffolding that held it up.",
    category: "Customer Success",
    date: "November 2024",
    readTime: "6 min read",
    body: [
      "In the GCC enterprise market, contracts are won on relationships and lost on reporting. Our 98% retention number didn't come from gifts or escalations — it came from a disciplined quarterly business review rhythm.",
      "Every QBR answered four questions for the client: what did you pay us, what did we deliver, what did it save you, and what are we doing next. No marketing slides. No vanity metrics. Just an honest ledger.",
      "The second lever was proactive risk mapping. We scored accounts monthly across contract, commercial, delivery and sponsor dimensions. Any amber triggered a named executive owner within 48 hours.",
      "Retention is a craft. You build it the way you build a cathedral — one stone, one promise kept, at a time."
    ]
  },
  {
    id: "a10",
    slug: "leading-teams-of-100",
    title: "Leading Teams of 100+ Without Losing the Craft",
    excerpt:
      "Span of control, skip‑levels and the quiet art of being the slowest person in the room.",
    category: "Leadership",
    date: "October 2024",
    readTime: "6 min read",
    body: [
      "Somewhere between 40 and 60 direct and indirect reports, leadership stops being about answers and starts being about architecture. Who decides what. Who escalates what. Who owns the ratio.",
      "I hold three non‑negotiables with my leadership line: a weekly one‑page operating update, a monthly skip‑level with their teams, and a quarterly career conversation with every manager. Rhythms, not heroics.",
      "The deeper lesson is cultural. In a team of 100+ across three countries, my job is rarely to be the smartest in the meeting — it's to be the slowest. To ask the question that forces clarity, and then get out of the way.",
      "Scale is earned by subtraction. Every time you remove yourself from a decision that shouldn't need you, you give your organisation another gear."
    ]
  },
  {
    id: "a11",
    slug: "vendor-partnerships-that-scale",
    title: "Vendor Partnerships That Actually Scale in the GCC",
    excerpt:
      "A framework for turning OEM logos on a slide into co‑invested, revenue‑generating relationships.",
    category: "Partnerships",
    date: "September 2024",
    readTime: "5 min read",
    body: [
      "Every integrator in the region has a wall of vendor logos. Very few have relationships that move the needle. The difference is intentional design.",
      "The partnerships that scaled for us — Kyocera, Microsoft, Nintex, HP, MyQ, SafeQ — shared three traits: a named joint‑business plan with quarterly reviews, co‑invested demand generation, and a clear escalation line above the account manager.",
      "The other unlock is selectivity. We deliberately shrank our partner list to those where we could be top‑quartile. Being important to a partner is worth more than being present with ten of them.",
      "Treat vendor strategy as a portfolio decision. Every partner should have a thesis, a budget, a champion — and a renewal criterion."
    ]
  },
  {
    id: "a12",
    slug: "pnl-mindset-for-technical-leaders",
    title: "The P&L Mindset for Technical Leaders",
    excerpt:
      "Four financial habits every service and solutions leader should build before the next budget cycle.",
    category: "Finance & Governance",
    date: "August 2024",
    readTime: "7 min read",
    body: [
      "Technical leaders are often promoted for delivery excellence and then measured on financial outcomes they were never taught. The gap is closable — if you build four habits.",
      "First, know your gross margin by contract, not just by division. Second, understand your revenue mix: recurring vs. project, services vs. products. Third, track utilisation and billability weekly, not quarterly. Fourth, meet finance before they have to chase you.",
      "Once these are muscle memory, the conversation with the CFO changes. You stop defending your budget and start co‑authoring the growth plan.",
      "The P&L is not a scoreboard. It's the most honest story your organisation tells about itself every month. Read it like a leader, not a spectator."
    ]
  }
];

export const navLinks = [
  { href: "#summary", label: "Summary" },
  { href: "#tracks", label: "Tracks" },
  { href: "#experience", label: "Experience" },
  { href: "#projects", label: "Projects" },
  { href: "#ecosystem", label: "Ecosystem" },
  { href: "#capabilities", label: "Capabilities" },
  { href: "#press", label: "Press" },
  { href: "#insights", label: "Insights" },
  { href: "#contact", label: "Contact" }
];
